<template>
  <div class="trait-configuration">
    <div class="header-section">
      <h1 class="page-title">Trait Configuration</h1>
      <p class="page-subtitle">
        Define and manage traits for leaf competencies
      </p>
    </div>

    <div class="main-content">
      <div class="content-layout">
        <!-- Left Panel -->
        <div class="left-panel">
          <div class="panel-header">
            <h3 class="panel-title">Select Competency</h3>
            <p class="panel-hint">
              Only competencies without children can have traits
            </p>
            <div class="search-box">
              <i class="bi bi-search"></i>
              <input type="text" placeholder="Search Competency..." v-model="competencySearchQuery" />
            </div>
          </div>

          <div class="competency-tree">
            <div v-for="competency in filteredRootCompetencies" :key="competency.id" class="tree-node">
              <!-- Root competency node -->
              <div class="competency-node" :class="{
                active: selectedCompetency?.id === competency.id,
                selectable: isLeafCompetency(competency.id),
                'has-children': hasChildren(competency.id),
              }" @click="selectCompetency(competency)">
                <div class="node-content">
                  <i v-if="hasChildren(competency.id)" class="chevron-icon bi" :class="isExpanded(competency.id)
                    ? 'bi-chevron-down'
                    : 'bi-chevron-right'
                    " @click.stop="toggleExpand(competency.id)">
                  </i>
                  <!-- <i v-else class="leaf-icon bi bi-circle-fill"></i> -->

                  <!-- Competency Icon based on type -->
                  <!-- <i
                    class="competency-type-icon bi"
                    :class="getCompetencyIcon(competency)"
                  ></i> -->

                  <span class="competency-name">{{ competency.name }}</span>

                  <!-- Leaf indicator -->
                  <!-- <span
                    v-if="isLeafCompetency(competency.id)"
                    class="leaf-badge"
                  >
                    Can add traits
                  </span> -->
                </div>
                <div class="competency-meta">
                  <span v-if="isLeafCompetency(competency.id)" class="topics-count selectable-count">
                    {{ getTopicCount(competency.id) }} traits
                  </span>
                  <span v-else class="topics-count parent-count">
                    {{ getChildrenCount(competency.id) }} sub-competencies
                  </span>
                  <span v-if="isLeafCompetency(competency.id)" class="weight-pill" :class="getWeightClass(getTotalWeightForCompetency(competency.id))
                    ">
                    {{ getTotalWeightForCompetency(competency.id) }}%
                  </span>
                </div>
              </div>

              <!-- Children rendering with proper indentation -->
              <div v-if="isExpanded(competency.id)" class="children-container">
                <div v-for="child in getFilteredChildren(competency.id)" :key="child.id" class="child-wrapper"
                  :style="{ paddingLeft: getIndentLevel(child) + 'px' }">
                  <div class="child-node" :class="{
                    active: selectedCompetency?.id === child.id,
                    selectable: isLeafCompetency(child.id),
                    'has-children': hasChildren(child.id),
                  }" @click="selectCompetency(child)">
                    <div class="child-content">
                      <div class="node-header">
                        <!-- Connection lines for tree structure -->
                        <div class="tree-lines">
                          <div class="vertical-line"></div>
                          <div class="horizontal-line"></div>
                        </div>

                        <i v-if="hasChildren(child.id)" class="chevron-icon bi" :class="isExpanded(child.id)
                          ? 'bi-chevron-down'
                          : 'bi-chevron-right'
                          " @click.stop="toggleExpand(child.id)">
                        </i>
                        <!-- <i v-else class="leaf-icon bi bi-circle-fill"></i> -->

                        <!-- Competency Icon -->
                        <i class="competency-type-icon bi" :class="getCompetencyIcon(child)"></i>

                        <span class="child-name">{{ child.name }}</span>

                        <!-- Leaf indicator -->
                        <!-- <span
                          v-if="isLeafCompetency(child.id)"
                          class="leaf-badge"
                        >
                          Can add traits
                        </span> -->
                      </div>
                      <div class="child-meta">
                        <span v-if="isLeafCompetency(child.id)" class="topics-count selectable-count">
                          {{ getTopicCount(child.id) }} traits
                        </span>
                        <span v-else class="topics-count parent-count">
                          {{ getChildrenCount(child.id) }} sub-competencies
                        </span>
                        <span v-if="isLeafCompetency(child.id)" class="weight-pill" :class="getWeightClass(
                          getTotalWeightForCompetency(child.id)
                        )
                          ">
                          {{ getTotalWeightForCompetency(child.id) }}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Recursive children for deeper levels -->
                  <div v-if="isExpanded(child.id) && hasChildren(child.id)" class="grandchildren-container">
                    <div v-for="grandchild in getFilteredChildren(child.id)" :key="grandchild.id"
                      class="grandchild-wrapper" :style="{
                        paddingLeft: getIndentLevel(grandchild) + 'px',
                      }">
                      <div class="child-node" :class="{
                        active: selectedCompetency?.id === grandchild.id,
                        selectable: isLeafCompetency(grandchild.id),
                        'has-children': hasChildren(grandchild.id),
                      }" @click="selectCompetency(grandchild)">
                        <div class="child-content">
                          <div class="node-header">
                            <!-- Connection lines -->
                            <div class="tree-lines">
                              <div class="vertical-line"></div>
                              <div class="horizontal-line"></div>
                            </div>

                            <i v-if="hasChildren(grandchild.id)" class="chevron-icon bi" :class="isExpanded(grandchild.id)
                              ? 'bi-chevron-down'
                              : 'bi-chevron-right'
                              " @click.stop="toggleExpand(grandchild.id)">
                            </i>
                            <!-- <i v-else class="leaf-icon bi bi-circle-fill"></i> -->

                            <!-- Competency Icon -->
                            <!-- <i
                              class="competency-type-icon bi"
                              :class="getCompetencyIcon(grandchild)"
                            ></i> -->

                            <span class="child-name">{{
                              grandchild.name
                            }}</span>

                            <!-- Leaf indicator -->
                            <!-- <span
                              v-if="isLeafCompetency(grandchild.id)"
                              class="leaf-badge"
                            >
                              Can add traits
                            </span> -->
                          </div>
                          <div class="child-meta">
                            <span v-if="isLeafCompetency(grandchild.id)" class="topics-count selectable-count">
                              {{ getTopicCount(grandchild.id) }} traits
                            </span>
                            <span v-else class="topics-count parent-count">
                              {{ getChildrenCount(grandchild.id) }}
                              sub-competencies
                            </span>
                            <span v-if="isLeafCompetency(grandchild.id)" class="weight-pill" :class="getWeightClass(
                              getTotalWeightForCompetency(grandchild.id)
                            )
                              ">
                              {{ getTotalWeightForCompetency(grandchild.id) }}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Legend -->
          <!-- <div class="competency-legend">
            <div class="legend-item">
              <i class="bi bi-circle-fill leaf-icon"></i>
              <span>Leaf competency (can add traits)</span>
            </div>
            <div class="legend-item">
              <i class="bi bi-chevron-right"></i>
              <span>Parent competency (has sub-competencies)</span>
            </div>
          </div> -->
        </div>

        <!-- Right Panel - Keep existing right panel code -->
        <div class="right-panel">
          <div v-if="!selectedCompetency" class="empty-state">
            <div class="empty-icon"><i class="bi bi-target"></i></div>
            <h3>Select a Leaf Competency</h3>
            <p>Choose a competency without children to manage its traits</p>
            <div class="selection-hint">
              <i class="bi bi-info-circle"></i>
              <span>Only leaf competencies (without sub-competencies) can have
                traits assigned</span>
            </div>
          </div>

          <div v-else-if="!isLeafCompetency(selectedCompetency.id)" class="invalid-selection">
            <div class="invalid-icon">
              <i class="bi bi-exclamation-triangle"></i>
            </div>
            <h3>Cannot Add Traits Here</h3>
            <p>
              <strong>"{{ selectedCompetency.name }}"</strong> has
              sub-competencies.
            </p>
            <p>
              Please select a leaf competency (one without children) to add
              traits.
            </p>
            <div class="suggestion">
              <p>
                <strong>Available leaf competencies under "{{
                  selectedCompetency.name
                }}":</strong>
              </p>
              <ul class="leaf-suggestions">
                <li v-for="leaf in getLeafCompetenciesUnder(
                  selectedCompetency.id
                )" :key="leaf.id" @click="selectCompetency(leaf)" class="leaf-suggestion-item">
                  <i class="bi bi-circle-fill"></i>
                  {{ leaf.name }}
                </li>
              </ul>
            </div>
          </div>

          <div v-else class="trait-section">
            <!-- Trait Header with Filters -->
            <div class="trait-header">
              <div class="header-left">
                <h3 class="trait-title">
                  Trait List for {{ selectedCompetency.name }}
                </h3>
                <span class="trait-count">{{ displayedTraits.length }} traits configured</span>
              </div>
              <div class="header-right">
                <!-- Filter & Actions Section -->
                <div class="filter-actions-container">
                  <button class="filter-btn" @click="toggleFilterPanel" :class="{ active: showFilterPanel }">
                    <i class="bi bi-funnel"></i>
                    Filters
                    <span v-if="selectedForFilter.length > 0" class="filter-count">{{ selectedForFilter.length }}</span>
                  </button>
                </div>

                <div class="search-container">
                  <i class="bi bi-search search-icon"></i>
                  <input type="text" class="search-input" placeholder="Search traits..." v-model="traitSearchQuery" />
                </div>
                <button class="add-btn" @click="openAddTraitModal">
                  Add Trait
                </button>
              </div>
            </div>

            <!-- Filter Panel -->
            <div v-if="showFilterPanel" class="filter-panel">
              <div class="filter-header">
                <h3>Bulk Actions</h3>
                <button class="close-filter-btn" @click="closeFilterPanel">
                  <i class="bi bi-x"></i>
                </button>
              </div>

              <div class="filter-content">
                <div class="filter-section">
                  <h4>Selected Traits ({{ selectedForFilter.length }})</h4>
                  <div class="selected-traits-list">
                    <div v-for="traitId in selectedForFilter" :key="traitId" class="selected-trait-item">
                      <span>{{ getTraitName(traitId) }}</span>
                      <button @click="removeFromFilter(traitId)" class="remove-btn">
                        <i class="bi bi-x"></i>
                      </button>
                    </div>
                    <div v-if="selectedForFilter.length === 0" class="no-selection">
                      No traits selected. Click checkboxes in the table to
                      select traits.
                    </div>
                  </div>
                </div>

                <div class="filter-actions">
                  <div class="action-group">
                    <label>Apply Equal Weightage:</label>
                    <div class="weight-input-group">
                      <input type="number" v-model.number="bulkWeight" min="1" max="100" placeholder="Weight %"
                        class="weight-input" />
                      <button @click="applyBulkWeight" :disabled="!canApplyBulkWeight" class="apply-btn">
                        Apply
                      </button>
                    </div>
                  </div>

                  <div class="action-group">
                    <label>Bulk Delete:</label>
                    <div class="delete-group">
                      <button @click="bulkDeleteTraits" :disabled="selectedForFilter.length === 0"
                        class="delete-btn-bulk">
                        <i class="bi bi-trash"></i>
                        Delete Selected ({{ selectedForFilter.length }})
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Weight Status Alerts -->
            <div v-if="weightValidationErrors.length > 0" class="weight-alert">
              <i class="bi bi-exclamation-triangle"></i>
              <div>
                <div v-for="error in weightValidationErrors" :key="error" class="weight-error-item">
                  {{ error }}
                </div>
              </div>
            </div>

            <!-- Table Container -->
            <div class="table-container">
              <table class="traits-table" v-if="displayedTraits.length > 0">
                <thead>
                  <tr>
                    <th class="checkbox-col" v-if="showFilterPanel">
                      <input type="checkbox" :checked="allTableItemsSelected" @change="toggleAllTableSelection"
                        class="table-checkbox" />
                    </th>
                    <th class="name-col">Trait Name</th>
                    <!-- <th class="desc-col">Description</th> -->
                    <th class="weight-col">Weight (%)</th>
                    <th class="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(trait, index) in displayedTraits" :key="trait.id" class="trait-row" :class="{
                    'weight-invalid': isWeightInvalid(),
                    unconfigured: trait.weight === 0,
                  }">
                    <td class="checkbox-col" v-if="showFilterPanel">
                      <input type="checkbox" :checked="selectedForFilter.includes(trait.id)"
                        @change="toggleFilterSelection(trait.id)" class="table-checkbox" />
                    </td>
                    <td class="name-col">
                      <div class="trait-name-container">
                        <span class="trait-name">{{ trait.name }}</span>
                      </div>
                    </td>
                    <!-- <td class="desc-col">
                      <span class="trait-description">{{
                        trait.description || "-"
                      }}</span>
                    </td> -->
                    <td class="weight-col">
                      <span class="weight-badge" :class="getTraitWeightClass(trait.weight)">{{ trait.weight }}%</span>
                    </td>
                    <td class="actions-col">
                      <div class="actions-container">
                        <button class="action-btn edit-btn" @click="editTrait(index)" title="Edit">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button class="action-btn delete-btn" @click="deleteTrait(index)" title="Remove">
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="displayedTraits.length === 0 && !traitSearchQuery" class="table-empty">
                <div class="empty-icon"><i class="bi bi-plus-circle"></i></div>
                <h4>No Traits Added</h4>
                <p>Add traits to this competency to get started</p>
                <button class="add-btn" @click="openAddTraitModal">
                  Add First Trait
                </button>
              </div>

              <div v-if="displayedTraits.length === 0 && traitSearchQuery" class="table-empty">
                <div class="empty-icon"><i class="bi bi-search"></i></div>
                <h4>No Traits Found</h4>
                <p>
                  No traits match your search criteria: "{{ traitSearchQuery }}"
                </p>
                <button class="btn-secondary" @click="traitSearchQuery = ''">
                  Clear Search
                </button>
              </div>
            </div>

            <!-- Table Footer -->
            <div class="table-footer">
              <div class="footer-left">
                <span class="weight-status" :class="weightStatusClass">
                  <i class="bi bi-check-circle-fill"></i>
                  Total Weight for {{ selectedCompetency.name }}:
                  {{ totalWeight }}% / 100%
                </span>
              </div>
            </div>

            <!-- Configure Section -->
            <div class="configure-section">
              <button class="configure-btn" @click="configureTrait" :disabled="!allCompetenciesComplete">
                {{
                  allCompetenciesComplete
                    ? "Configure Trait"
                    : `Complete All Competencies (${incompleteCount} remaining)`
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Trait Modal -->
    <div v-if="showAddTraitModal" class="modal-overlay" @click="closeAddTraitModal">
      <div class="add-modal-content" @click.stop>
        <div class="modal-header">
          <h3>Add Trait to {{ selectedCompetency?.name }}</h3>
          <button class="close-btn" @click="closeAddTraitModal">
            <i class="bi bi-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Search Bar -->
          <div class="search-section">
            <div class="search-container-full">
              <i class="bi bi-search search-icon"></i>
              <input type="text" class="search-input-full" placeholder="Search traits from database..."
                v-model="modalTraitSearch" @input="onSearchInput" @keydown.enter="onSearchEnter" />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button class="create-trait-btn" @click="openCreateTraitModal">
              <i class="bi bi-plus-circle"></i>
              Create Trait
            </button>
            <button class="ai-generate-btn" @click="generateTraitsWithAI">
              <i class="bi bi-magic"></i>
              Generate with AI
            </button>
          </div>

          <!-- Trait Cards Grid -->
          <div class="trait-grid">
            <div v-for="trait in paginatedDatabaseTraits" :key="trait.id" class="trait-card"
              :class="{ selected: isTraitSelected(trait.id) }" @click="toggleTraitSelection(trait.id)">
              <div class="trait-info">
                <h4 class="card-title">{{ trait.name }}</h4>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredDatabaseTraits.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="bi bi-search"></i>
            </div>

            <template v-if="modalTraitSearch.trim().length > 0">
              <template v-if="!searchFlag && filteredDatabaseTraits.length === 0">
                <h4>Searching for "{{ modalTraitSearch }}"...</h4>
                <p>Please wait while we fetch results.</p>
              </template>

              <template v-if="searchFlag && filteredDatabaseTraits.length === 0">
                <h4>No results for "{{ modalTraitSearch }}"</h4>
                <p>Try a different search term.</p>
              </template>

            </template>

            <template v-else>
              <h4>No traits found</h4>
              <p>Try adjusting your search or create a new trait</p>
            </template>
          </div>

          <!-- Pagination -->
          <div v-if="filteredDatabaseTraits.length > 0" class="pagination-section">
            <div class="pagination-info">
              Showing {{ traitStartIndex + 1 }}-{{ traitEndIndex }}
              of {{ totaltraits }} traits
            </div>
            <div class="pagination-controls">
              <button class="pagination-btn" @click="previousTraitPage" :disabled="traitCurrentPage === 1">
                <i class="bi bi-chevron-left"></i>
                Previous
              </button>
              <span class="page-indicator">{{ traitCurrentPage }} of {{ traitTotalPages }}</span>
              <button class="pagination-btn" @click="nextTraitPage" :disabled="traitCurrentPage === traitTotalPages">
                Next
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <div class="footer-left">
            <button class="select-all-btn" @click="toggleSelectAllTraits">
              <i class="bi bi-check2-all"></i>
              {{ allTraitsSelected ? "Deselect All" : "Select All" }}
            </button>
            <span class="selection-count">{{ selectedTraits.length }} selected</span>
          </div>
          <div class="footer-right">
            <button class="cancel-btn" @click="closeAddTraitModal">
              Cancel
            </button>
            <button class="add-selected-btn" @click="addSelectedTraits" :disabled="selectedTraits.length === 0">
              Add Selected ({{ selectedTraits.length }})
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit/Create Trait Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop ref="modalDialog">
        <div class="modal-header">
          <div class="edit-header-content">
            <h3>{{ editingTrait ? "Edit" : "Create" }} Trait</h3>
            <div v-if="editingTrait" class="simple-info">
              <div class="simple-info-line">
                <span class="simple-label">Competency:</span>
                <span class="simple-value">{{
                  selectedCompetency?.name || "N/A"
                }}</span>
              </div>
            </div>
          </div>
          <button class="close-btn" @click="closeModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Trait Name *</label>
            <input type="text" v-model="traitForm.name" placeholder="Enter trait name" class="modal-input"
              maxlength="100" />
            <div v-if="nameError" class="field-error">{{ nameError }}</div>
          </div>

          <div class="form-group" v-if="editingTrait">
            <label>Weight (%) *</label>
            <input type="number" v-model.number="traitForm.weight" placeholder="Enter weight" class="modal-input"
              min="1" max="100" />
            <div class="weight-info">
              <span class="current-total">Current total for this competency:
                {{ currentCompetencyWeight }}%</span>
            </div>
            <div v-if="weightError" class="field-error">{{ weightError }}</div>
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea v-model="traitForm.description" placeholder="Enter description" class="modal-input modal-textarea"
              rows="3" maxlength="500"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Cancel</button>
          <button class="save-btn" @click="saveTrait" :disabled="!canSave">
            {{ editingTrait ? "Update" : "Create" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content delete-modal" @click.stop>
        <div class="modal-header">
          <h3>Delete Trait</h3>
          <button class="close-btn" @click="closeDeleteModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="delete-content">
            <div class="delete-icon">
              <i class="bi bi-exclamation-triangle"></i>
            </div>
            <div class="delete-message">
              <h4>Are you sure you want to delete this trait?</h4>
              <p>
                <strong>"{{ deletingTrait?.name }}"</strong>
              </p>
              <p>
                This action cannot be undone. The trait will be permanently
                removed from the competency.
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeDeleteModal">Cancel</button>
          <button class="delete-confirm-btn" @click="confirmDeleteTrait">
            <i class="bi bi-trash"></i>
            Delete Trait
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { CreateNewTrait } from "../../api/api";
export default {
  name: "TraitConfiguration",
  props: {
    formData: {
      type: Object,
      default: () => ({ topics: [] }),
    },
  },
  emits: ["update-data", "step-completed"],
  data() {
    return {
      selectedCompetency: null,
      selectedTraitIds: [],
      expandedNodes: [1],
      competencySearchQuery: "",
      traitSearchQuery: "",
      modalTraitSearch: "",
      showModal: false,
      showAddTraitModal: false,
      showDeleteModal: false,
      editingTrait: null,
      deletingTrait: null,
      competencies: [],

      // Filter Panel
      showFilterPanel: false,
      selectedForFilter: [],
      bulkWeight: null,

      // Add Trait Modal Data
      selectedTraits: [],
      traitCurrentPage: 1,
      traitItemsPerPage: 10,
      traitTotalPages: null,
      totaltraits: null,
      searchFlag: false,

      // Database traits (dummy data)
      // databaseTraits: [
      //   {
      //     id: 1,
      //     name: "Critical Thinking",
      //     description:
      //       "Ability to analyze information objectively and make reasoned judgments",
      //   },
      //   {
      //     id: 2,
      //     name: "Emotional Intelligence",
      //     description:
      //       "Understanding and managing emotions effectively in self and others",
      //   },
      //   {
      //     id: 3,
      //     name: "Resilience",
      //     description:
      //       "Ability to bounce back from setbacks and adapt to challenges",
      //   },
      //   {
      //     id: 4,
      //     name: "Innovation",
      //     description: "Capacity to generate creative solutions and new ideas",
      //   },
      //   {
      //     id: 5,
      //     name: "Integrity",
      //     description:
      //       "Adherence to moral and ethical principles in all situations",
      //   },
      // ],

      databaseTraits: [],
      traits: [],

      traitForm: {
        name: "",
        description: "",
        weight: null,
      },
    };
  },
  computed: {
    // Filter root competencies based on search
    filteredRootCompetencies() {
      const roots = this.competencies.filter((comp) => comp.level === 1);
      if (!this.competencySearchQuery.trim()) {
        return roots;
      }
      const query = this.competencySearchQuery.toLowerCase().trim();
      return roots.filter(
        (comp) =>
          comp.name.toLowerCase().includes(query) ||
          this.hasMatchingDescendant(comp.id, query)
      );
    },

    displayedTraits() {
      if (!this.selectedCompetency) return [];
      let traits = this.traits.filter(
        (trait) => trait.competencyId === this.selectedCompetency.id
      );
      if (this.traitSearchQuery.trim()) {
        const query = this.traitSearchQuery.toLowerCase().trim();
        traits = traits.filter(
          (trait) =>
            trait.name.toLowerCase().includes(query) ||
            (trait.description &&
              trait.description.toLowerCase().includes(query))
        );
      }
      return traits;
    },

    // Filter computeds
    canApplyBulkWeight() {
      return (
        this.selectedForFilter.length > 0 &&
        this.bulkWeight &&
        this.bulkWeight >= 1 &&
        this.bulkWeight <= 100
      );
    },

    allTableItemsSelected() {
      return (
        this.displayedTraits.length > 0 &&
        this.displayedTraits.every((trait) =>
          this.selectedForFilter.includes(trait.id)
        )
      );
    },

    totalTraitsCount() {
      return this.displayedTraits.length;
    },

    weightValidationErrors() {
      const errors = [];
      if (this.selectedCompetency && this.totalWeight !== 100) {
        errors.push(
          `Traits for "${this.selectedCompetency.name}" must total 100% (Current: ${this.totalWeight}%)`
        );
      }
      return errors;
    },

    allSelected() {
      return (
        this.displayedTraits.length > 0 &&
        this.selectedTraitIds.length === this.displayedTraits.length
      );
    },

    totalWeight() {
      return this.displayedTraits.reduce((sum, trait) => sum + trait.weight, 0);
    },

    currentCompetencyWeight() {
      if (!this.selectedCompetency) return 0;
      return this.traits
        .filter((trait) => trait.competencyId === this.selectedCompetency.id)
        .reduce((sum, trait) => sum + (trait.weight || 0), 0);
    },

    weightStatusClass() {
      return this.totalWeight === 100 ? "weight-perfect" : "weight-incomplete";
    },

    nameError() {
      return this.traitForm.name && this.traitForm.name.length < 2
        ? "Name must be at least 2 characters"
        : null;
    },

    weightError() {
      if (
        this.traitForm.weight &&
        (this.traitForm.weight < 1 || this.traitForm.weight > 100)
      ) {
        return "Weight must be between 1 and 100";
      }
      if (this.willExceedWeight) {
        return `This would exceed 100% (current: ${this.totalWeight}%)`;
      }
      return null;
    },

    willExceedWeight() {
      if (!this.traitForm.weight || !this.selectedCompetency) return false;
      let currentTotal = this.totalWeight;
      if (this.editingTrait) currentTotal -= this.editingTrait.weight;
      return currentTotal + this.traitForm.weight > 100;
    },

    canSave() {
      if (this.editingTrait) {
        return (
          this.traitForm.name &&
          this.traitForm.name.length >= 2 &&
          this.traitForm.weight &&
          this.traitForm.weight >= 1 &&
          this.traitForm.weight <= 100 &&
          !this.willExceedWeight
        );
      } else {
        return this.traitForm.name && this.traitForm.name.length >= 2;
      }
    },

    allCompetenciesComplete() {
      return this.checkAllCompetenciesHave100Percent().allComplete;
    },

    incompleteCount() {
      return this.checkAllCompetenciesHave100Percent().incompleteCompetencies
        .length;
    },

    // Add Trait Modal Computed Properties
    filteredDatabaseTraits() {
      if (!this.modalTraitSearch?.trim()) {
        return this.databaseTraits;
      }
      const query = this.modalTraitSearch.toLowerCase().trim();
      if (query.length > 0 && this.searchFlag) {
        return this.databaseTraits.filter(
          (trait) =>
            trait.name.toLowerCase().includes(query) ||
            trait.description.toLowerCase().includes(query)
        );
      }
      else {
        return []
      }
    },

    // traitTotalPages() {
    //   return Math.ceil(
    //     this.filteredDatabaseTraits.length / this.traitItemsPerPage
    //   );
    // },

    traitStartIndex() {
      return (this.traitCurrentPage - 1) * this.traitItemsPerPage;
    },

    traitEndIndex() {
      return Math.min(this.totaltraits, this.traitStartIndex + this.traitItemsPerPage);
    },

    paginatedDatabaseTraits() {
      return this.filteredDatabaseTraits.slice(
        0, this.itemsPerPage
      );
    },

    allTraitsSelected() {
      return (
        this.paginatedDatabaseTraits.length > 0 &&
        this.paginatedDatabaseTraits.every((trait) =>
          this.isTraitSelected(trait.id)
        )
      );
    },
  },
  async mounted() {
    this.databaseTraits = await this.getTraitData()
    this.loadCompetencies();
    this.loadTraitsData();
    this.autoSelectFirstChild();
    document.addEventListener("keydown", this.handleEscapeKey);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscapeKey);
  },
  watch: {
    formData: {
      handler(newFormData) {
        console.log("📥 Received formData from parent:", newFormData);
        if (newFormData && newFormData.topics) {
          this.competencies = this.ensureDescriptorStatus([
            ...newFormData.topics,
          ]);
          console.log(
            "✅ Updated competencies from formData:",
            this.competencies.map((c) => c.name)
          );

          if (!this.isStepCompleted() || !this.selectedCompetency) {
            this.autoSelectFirstChild();
          }
        }
      },
      deep: true,
      immediate: true,
    },

    modalTraitSearch() {
      this.traitCurrentPage = 1;
    },
  },
  methods: {
    // **NEW DESCRIPTOR STATUS METHODS**

    ...mapActions(["GetTraitData", "CreateNewTrait"]),

    onSearchInput() {
      this.searchFlag = false;
    },

    async onSearchEnter() {
      this.searchFlag = false
      this.currentPage = 1;
      this.databaseTraits = await this.getTraitData()
      this.searchFlag = true
    },

    async getTraitData() {
      this.databaseTraits = []
      var response = await this.GetTraitData({
        pageNumber: this.traitCurrentPage,
        pageSize: this.traitItemsPerPage,
        searchStr: this.modalTraitSearch || "",
      });

      const traitResponse = response?.object?.data
      if (traitResponse) {
        this.traitTotalPages = traitResponse?.totalPages
        this.currentPage = traitResponse.currentPage
        this.totaltraits = traitResponse.totalItems
        return traitResponse?.traits.map(item => {
          return {
            id: item.traitId,
            name: item.traitDisplayName,
            description: item.description,
            // displaName: item.traitDisplayName,
          }
        })
      }
      return []
    },


    traceDescriptorHierarchy(competencyId) {
      let current = this.competencies.find((c) => c.id === competencyId);
      let descriptorStatusInternal = false;
      let iterationCount = 0;

      while (current && iterationCount < 5) {
        if (current.descriptorStatus) {
          descriptorStatusInternal = true;
          break;
        }

        if (current.parentId != null) {
          current = this.competencies.find((c) => c.id === current.parentId);
        } else {
          current = null;
        }

        iterationCount++;
      }

      return descriptorStatusInternal;
    },

    // Ensure all competencies have descriptorStatus property
    ensureDescriptorStatus(competencies) {
      return competencies.map((comp) => ({
        ...comp,
        descriptorStatus:
          comp.descriptorStatus !== undefined ? comp.descriptorStatus : true,
      }));
    },

    // Ensure all traits have descriptorStatus property
    ensureTraitDescriptorStatus(traits) {
      return traits.map((trait) => ({
        ...trait,
        descriptorStatus:
          trait.descriptorStatus !== undefined ? trait.descriptorStatus : true,
      }));
    },

    // **STEP COMPLETION TRACKING METHODS**

    isStepCompleted() {
      try {
        const completionStatus = localStorage.getItem("stepCompletionStatus");
        if (completionStatus) {
          const status = JSON.parse(completionStatus);
          return status.traitConfiguration === true;
        }

        const traitData = localStorage.getItem("traitConfiguration");
        if (traitData) {
          const data = JSON.parse(traitData);
          return (
            data.completed === true || (data.traits && data.traits.length > 0)
          );
        }

        return false;
      } catch (error) {
        console.warn("Error checking step completion status:", error);
        return false;
      }
    },

    loadTraitsData() {
      console.log("🔄 Loading traits data...");

      if (this.isStepCompleted()) {
        console.log(
          "✅ Step completed, loading existing traits from localStorage"
        );
        try {
          const traitData = localStorage.getItem("traitConfiguration");
          if (traitData) {
            const data = JSON.parse(traitData);
            if (data.traits) {
              this.traits = this.ensureTraitDescriptorStatus(data.traits);
              console.log(
                "✅ Loaded existing traits from localStorage:",
                this.traits.length
              );
              return;
            }
          }
        } catch (error) {
          console.warn("Error loading traits from localStorage:", error);
        }

        this.traits = [];
        return;
      }

      try {
        const traitData = localStorage.getItem("traitConfiguration");
        if (traitData) {
          const data = JSON.parse(traitData);
          if (data.traits) {
            this.traits = this.ensureTraitDescriptorStatus(data.traits);
            console.log(
              "✅ Loaded traits from localStorage:",
              this.traits.length
            );
          }
        }
      } catch (error) {
        console.warn("Error loading traits from localStorage:", error);
        this.traits = [];
      }
    },

    markStepAsCompleted() {
      try {
        const completionStatus = JSON.parse(
          localStorage.getItem("stepCompletionStatus") || "{}"
        );
        completionStatus.traitConfiguration = true;
        localStorage.setItem(
          "stepCompletionStatus",
          JSON.stringify(completionStatus)
        );

        const traitData = {
          traits: this.traits,
          competencies: this.competencies,
          completed: true,
          completedAt: new Date().toISOString(),
        };
        localStorage.setItem("traitConfiguration", JSON.stringify(traitData));

        console.log("✅ Step marked as completed");
      } catch (error) {
        console.warn("Error marking step as completed:", error);
      }
    },

    resetStepCompletion() {
      try {
        const completionStatus = JSON.parse(
          localStorage.getItem("stepCompletionStatus") || "{}"
        );
        delete completionStatus.traitConfiguration;
        localStorage.setItem(
          "stepCompletionStatus",
          JSON.stringify(completionStatus)
        );

        const traitData = localStorage.getItem("traitConfiguration");
        if (traitData) {
          const data = JSON.parse(traitData);
          delete data.completed;
          delete data.completedAt;
          localStorage.setItem("traitConfiguration", JSON.stringify(data));
        }

        console.log("✅ Step completion reset");
      } catch (error) {
        console.warn("Error resetting step completion:", error);
      }
    },

    // **EXISTING METHODS (updated with descriptor status)**

    getIndentLevel(competency) {
      return (competency.level - 1) * 25;
    },

    hasMatchingDescendant(parentId, query) {
      const children = this.competencies.filter(
        (comp) => comp.parentId === parentId
      );
      return children.some(
        (child) =>
          child.name.toLowerCase().includes(query) ||
          this.hasMatchingDescendant(child.id, query)
      );
    },

    getFilteredChildren(parentId) {
      const children = this.competencies.filter(
        (comp) => comp.parentId === parentId
      );
      if (!this.competencySearchQuery.trim()) {
        return children;
      }
      const query = this.competencySearchQuery.toLowerCase().trim();
      return children.filter(
        (child) =>
          child.name.toLowerCase().includes(query) ||
          this.hasMatchingDescendant(child.id, query)
      );
    },

    getTotalWeightForCompetency(competencyId) {
      const competencyTraits = this.traits.filter(
        (trait) => trait.competencyId === competencyId
      );
      return competencyTraits.reduce((sum, trait) => sum + trait.weight, 0);
    },

    toggleFilterPanel() {
      this.showFilterPanel = !this.showFilterPanel;
    },

    closeFilterPanel() {
      this.showFilterPanel = false;
      this.selectedForFilter = [];
    },

    toggleFilterSelection(traitId) {
      const index = this.selectedForFilter.indexOf(traitId);
      if (index > -1) {
        this.selectedForFilter.splice(index, 1);
      } else {
        this.selectedForFilter.push(traitId);
      }
    },

    toggleAllTableSelection() {
      if (this.allTableItemsSelected) {
        this.selectedForFilter = [];
      } else {
        this.selectedForFilter = [...this.displayedTraits.map((t) => t.id)];
      }
    },

    removeFromFilter(traitId) {
      const index = this.selectedForFilter.indexOf(traitId);
      if (index > -1) {
        this.selectedForFilter.splice(index, 1);
      }
    },

    getTraitName(traitId) {
      const trait = this.traits.find((t) => t.id === traitId);
      return trait ? trait.name : "Unknown";
    },

    applyBulkWeight() {
      if (!this.canApplyBulkWeight) return;

      this.selectedForFilter.forEach((traitId) => {
        const index = this.traits.findIndex(
          (t) =>
            t.id === traitId && t.competencyId === this.selectedCompetency.id
        );
        if (index > -1) {
          this.traits.splice(index, 1, {
            ...this.traits[index],
            weight: this.bulkWeight,
          });
        }
      });

      this.showNotification(
        `Applied ${this.bulkWeight}% weight to ${this.selectedForFilter.length} traits`,
        "success"
      );
      this.selectedForFilter = [];
      this.bulkWeight = null;
    },

    bulkDeleteTraits() {
      if (this.selectedForFilter.length === 0) return;

      if (
        confirm(
          `Are you sure you want to delete ${this.selectedForFilter.length} selected traits? This action cannot be undone.`
        )
      ) {
        this.traits = this.traits.filter(
          (trait) =>
            !(
              this.selectedForFilter.includes(trait.id) &&
              trait.competencyId === this.selectedCompetency.id
            )
        );
        this.showNotification(
          `Deleted ${this.selectedForFilter.length} traits`,
          "warning"
        );
        this.selectedForFilter = [];
      }
    },

    isWeightInvalid() {
      return this.totalWeight !== 100;
    },

    loadCompetencies() {
      console.log("🔄 Loading competencies from various sources...");

      if (
        this.formData &&
        this.formData.topics &&
        this.formData.topics.length > 0
      ) {
        this.competencies = this.ensureDescriptorStatus([
          ...this.formData.topics,
        ]);
        console.log(
          "✅ Loaded competencies from formData props:",
          this.competencies.map((c) => c.name)
        );
        return;
      }

      try {
        const subjectState = localStorage.getItem("subjectManagementState");
        if (subjectState) {
          const state = JSON.parse(subjectState);
          if (
            state.stepData &&
            state.stepData.topicsManagement &&
            state.stepData.topicsManagement.topics &&
            state.stepData.topicsManagement.topics.length > 0
          ) {
            this.competencies = this.ensureDescriptorStatus(
              state.stepData.topicsManagement.topics
            );
            console.log(
              "✅ Loaded competencies from subjectManagementState:",
              this.competencies.map((c) => c.name)
            );
            return;
          }
        }
      } catch (error) {
        console.warn(
          "Error loading competencies from subjectManagementState:",
          error
        );
      }

      try {
        const traitConfig = localStorage.getItem("traitConfiguration");
        if (traitConfig) {
          const config = JSON.parse(traitConfig);
          if (config.competencies && config.competencies.length > 0) {
            this.competencies = this.ensureDescriptorStatus(
              config.competencies
            );
            console.log(
              "✅ Loaded competencies from traitConfiguration:",
              this.competencies.map((c) => c.name)
            );
            return;
          }
        }
      } catch (error) {
        console.warn(
          "Error loading competencies from traitConfiguration:",
          error
        );
      }

      try {
        const storedData = localStorage.getItem("topicsManagement");
        if (storedData) {
          const parsed = JSON.parse(storedData);
          if (parsed.topics && parsed.topics.length > 0) {
            this.competencies = this.ensureDescriptorStatus(parsed.topics);
            console.log(
              "✅ Loaded competencies from legacy topicsManagement:",
              this.competencies.map((c) => c.name)
            );
            return;
          }
        }
      } catch (error) {
        console.warn(
          "Error loading competencies from legacy topicsManagement:",
          error
        );
      }

      console.warn(
        "⚠️ No competency data found anywhere, starting with empty array"
      );
      this.competencies = [];
    },

    handleEscapeKey(event) {
      if (event.key === "Escape") {
        if (this.showModal) {
          this.closeModal();
        } else if (this.showDeleteModal) {
          this.closeDeleteModal();
        } else if (this.showAddTraitModal) {
          this.closeAddTraitModal();
        }
      }
    },

    hasChildren(competencyId) {
      return this.competencies.some((comp) => comp.parentId === competencyId);
    },

    isLeafCompetency(competencyId) {
      return !this.hasChildren(competencyId);
    },

    getChildrenCount(competencyId) {
      return this.competencies.filter((comp) => comp.parentId === competencyId)
        .length;
    },

    getLeafCompetenciesUnder(parentId) {
      const allDescendants = this.getAllDescendants(parentId);
      return allDescendants.filter((comp) => this.isLeafCompetency(comp.id));
    },

    getAllDescendants(parentId) {
      const descendants = [];
      const directChildren = this.competencies.filter(
        (comp) => comp.parentId === parentId
      );

      for (const child of directChildren) {
        descendants.push(child);
        descendants.push(...this.getAllDescendants(child.id));
      }

      return descendants;
    },

    getCompetencyIcon(competency) {
      if (this.isLeafCompetency(competency.id)) {
        return "bi-bookmark-fill";
      } else if (competency.level === 1) {
        return "bi-folder-fill";
      } else {
        return "bi-folder2-open";
      }
    },

    selectCompetency(competency) {
      if (!competency) {
        this.showNotification("Please select a valid competency", "warning");
        return;
      }

      if (!this.isLeafCompetency(competency.id)) {
        this.selectedCompetency = competency;
        this.showNotification(
          `"${competency.name}" has sub-competencies. Please select a leaf competency to add traits.`,
          "warning"
        );
        return;
      }

      this.selectedCompetency = competency;
      this.selectedTraitIds = [];
      this.selectedForFilter = [];

      // Apply descriptor status rules when selecting competency
      this.applyDescriptorStatusRules(competency.id);

      this.showNotification(
        `Selected leaf competency: ${competency.name}`,
        "success"
      );
    },

    autoSelectFirstChild() {
      console.log("🎯 Auto-selecting first leaf competency...");

      if (this.competencies.length === 0) {
        console.warn("⚠️ No competencies available to select");
        this.selectedCompetency = null;
        return;
      }

      const firstLeaf = this.competencies.find((comp) =>
        this.isLeafCompetency(comp.id)
      );
      if (firstLeaf) {
        this.selectedCompetency = firstLeaf;
        console.log("✅ Selected first leaf competency:", firstLeaf.name);
        return;
      }

      console.warn(
        "⚠️ No leaf competencies found - all competencies have children"
      );
      this.selectedCompetency = null;
    },

    isExpanded(competencyId) {
      return this.expandedNodes.includes(competencyId);
    },

    toggleExpand(competencyId) {
      const index = this.expandedNodes.indexOf(competencyId);
      if (index > -1) {
        this.expandedNodes.splice(index, 1);
      } else {
        this.expandedNodes.push(competencyId);
      }
    },

    getChildren(parentId) {
      return this.competencies.filter((comp) => comp.parentId === parentId);
    },

    getTopicCount(competencyId) {
      return this.traits.filter((trait) => trait.competencyId === competencyId)
        .length;
    },

    getWeightClass(weight) {
      return weight >= 50 ? "high" : weight >= 25 ? "medium" : "low";
    },

    getTraitWeightClass(weight) {
      return weight >= 50
        ? "weight-high"
        : weight >= 30
          ? "weight-medium"
          : "weight-low";
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedTraitIds = [];
      } else {
        this.selectedTraitIds = this.displayedTraits.map((trait) => trait.id);
      }
    },

    openAddTraitModal() {
      if (!this.selectedCompetency) {
        this.showNotification("Please select a competency first", "warning");
        return;
      }
      this.showAddTraitModal = true;
      this.selectedTraits = [];
      this.modalTraitSearch = "";
      this.traitCurrentPage = 1;
    },

    closeAddTraitModal() {
      this.showAddTraitModal = false;
      this.selectedTraits = [];
      this.modalTraitSearch = "";
    },

    isTraitSelected(traitId) {
      return this.selectedTraits.includes(traitId);
    },

    toggleTraitSelection(traitId) {
      const index = this.selectedTraits.indexOf(traitId);
      if (index > -1) {
        this.selectedTraits.splice(index, 1);
      } else {
        this.selectedTraits.push(traitId);
      }
    },

    toggleSelectAllTraits() {
      if (this.allTraitsSelected) {
        this.selectedTraits = this.selectedTraits.filter(
          (id) => !this.paginatedDatabaseTraits.some((trait) => trait.id === id)
        );
      } else {
        this.paginatedDatabaseTraits.forEach((trait) => {
          if (!this.isTraitSelected(trait.id)) {
            this.selectedTraits.push(trait.id);
          }
        });
      }
    },

    async previousTraitPage() {
      if (this.traitCurrentPage > 1) {
        this.traitCurrentPage--;
        this.databaseTraits = await this.getTraitData();
      }
    },

    async nextTraitPage() {
      if (this.traitCurrentPage < this.traitTotalPages) {
        this.traitCurrentPage++;
        this.databaseTraits = [];
        const data = await this.getTraitData();
        this.databaseTraits = data;
      }
    },

    generateTraitsWithAI() {
      this.showNotification("AI trait generation feature coming soon!", "info");
    },

    openCreateTraitModal() {
      this.editingTrait = null;
      this.resetForm();
      this.showModal = true;
    },

    addSelectedTraits() {
      if (this.selectedTraits.length === 0) return;

      const selectedDbTraits = this.databaseTraits.filter((trait) =>
        this.selectedTraits.includes(trait.id)
      );
      const rules = this.traceDescriptorHierarchy(this.selectedCompetency.id);

      selectedDbTraits.forEach((dbTrait) => {
        const newTrait = {
          id: dbTrait.id,
          competencyId: this.selectedCompetency.id,
          name: dbTrait.name,
          description: dbTrait.description,
          weight: 0,
          descriptorStatus: !rules,
        };
        this.traits.push(newTrait);
      });

      this.closeAddTraitModal();
      this.showNotification(
        `${selectedDbTraits.length} traits added! Please use the Edit button to configure weights and details.`,
        "success"
      );
    },

    editTrait(index) {
      if (!this.selectedCompetency) {
        this.showNotification("Please select a competency first", "warning");
        return;
      }
      const trait = this.displayedTraits[index];
      const actualIndex = this.traits.findIndex(
        (t) =>
          t.id === trait.id && t.competencyId === this.selectedCompetency.id
      );

      this.editingTrait = this.traits[actualIndex];
      this.traitForm = {
        name: trait.name,
        description: trait.description || "",
        weight: trait.weight,
      };
      this.showModal = true;

      this.$nextTick(() => {
        const firstInput =
          this.$refs.modalDialog?.querySelector('input[type="text"]');
        if (firstInput) firstInput.focus();
      });
    },

    deleteTrait(index) {
      const trait = this.displayedTraits[index];
      this.deletingTrait = trait;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deletingTrait = null;
    },

    confirmDeleteTrait() {
      if (!this.deletingTrait) return;

      const index = this.traits.findIndex(
        (t) =>
          t.id === this.deletingTrait.id &&
          t.competencyId === this.selectedCompetency.id
      );
      if (index > -1) {
        this.traits.splice(index, 1);
        this.showNotification(
          `Trait "${this.deletingTrait.name}" deleted successfully`,
          "success"
        );
      }

      const selectedIndex = this.selectedTraitIds.indexOf(
        this.deletingTrait.id
      );
      if (selectedIndex > -1) {
        this.selectedTraitIds.splice(selectedIndex, 1);
      }

      this.closeDeleteModal();
    },

    closeModal() {
      this.showModal = false;
      this.editingTrait = null;
      this.resetForm();
    },

    resetForm() {
      this.traitForm = {
        name: "",
        description: "",
        weight: null,
      };
    },

    async saveTrait() {
      if (!this.canSave) {
        this.showNotification(
          "Please fill all required fields correctly",
          "error"
        );
        return;
      }

      if (this.editingTrait) {
        const trait = {
          id: this.editingTrait.id,
          competencyId: this.selectedCompetency.id,
          name: this.traitForm.name.trim(),
          description: this.traitForm.description.trim(),
          weight: this.traitForm.weight,
          descriptorStatus: this.editingTrait.descriptorStatus,
        };

        const index = this.traits.findIndex(
          (t) =>
            t.id === this.editingTrait.id &&
            t.competencyId === this.selectedCompetency.id
        );
        if (index > -1) {
          this.traits.splice(index, 1, trait);
          this.showNotification("Trait updated successfully", "success");
        }
      } else {

        const payload = { traitName: this.traitForm.name.trim(), traitType: 1, description: this.traitForm?.description || "" }
        const newTrait = await CreateNewTrait(payload)
        if (newTrait) {
          const newDatabaseTrait = {
            id: newTrait.traitId,
            name: this.traitForm.name.trim(),
            description: this.traitForm.description.trim(),
            isCustom: true,
          };

          console.log("newDatabaseTrait:::",newDatabaseTrait)

          this.databaseTraits.push(newDatabaseTrait);

          this.databaseTraits.sort((a, b) => {
            if (a.isCustom && !b.isCustom) return -1;
            if (!a.isCustom && b.isCustom) return 1;
            return b.usageCount - a.usageCount;
          });

          this.showNotification(
            `New trait "${newDatabaseTrait.name}" created successfully! You can now select it from the trait list.`,
            "success"
          );

          if (this.showAddTraitModal) {
            this.selectedTraits.push(newDatabaseTrait.id);
            this.traitCurrentPage = 1;
          }
        } else {
          this.showNotification(
            `New trait "${this.traitForm.name.trim()}" not saved successfully.`,
            "error"
          );
        }
      }

      this.closeModal();
    },

    configureTrait() {
      const competencyWeightCheck = this.checkAllCompetenciesHave100Percent();

      if (!competencyWeightCheck.allComplete) {
        const incompleteList = competencyWeightCheck.incompleteCompetencies
          .map((comp) => `${comp.name} (${comp.currentWeight}%)`)
          .join(", ");

        this.showNotification(
          `Each competency must have 100% trait weight allocation before proceeding.\n\nIncomplete competencies:\n${incompleteList}`,
          "error"
        );
        return;
      }

      const traitData = {
        traits: this.traits,
        competencies: this.competencies,
      };

      this.$emit("update-data", {
        stepKey: "traitConfiguration",
        data: traitData,
      });

      this.markStepAsCompleted();

      this.showNotification(
        "Trait Configuration completed! All competencies have 100% weight allocation.",
        "success"
      );
      this.$emit("step-completed", 3);
    },

    checkAllCompetenciesHave100Percent() {
      const incompleteCompetencies = [];

      for (const competency of this.competencies) {
        if (this.isLeafCompetency(competency.id)) {
          const competencyTraits = this.traits.filter(
            (trait) => trait.competencyId === competency.id
          );

          const totalWeight = competencyTraits.reduce(
            (sum, trait) => sum + trait.weight,
            0
          );

          if (totalWeight !== 100) {
            incompleteCompetencies.push({
              ...competency,
              currentWeight: totalWeight,
              missingWeight: 100 - totalWeight,
            });
          }
        }
      }

      return {
        allComplete: incompleteCompetencies.length === 0,
        incompleteCompetencies,
      };
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          info: "alert-info",
          warning: "alert-warning",
        }[type] || "alert-info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText =
        "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
      notification.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 5000);
    },
  },
};
</script>

<style scoped>
.trait-configuration {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 2rem;
}

.header-section {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.main-content {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.content-layout {
  display: grid;
  grid-template-columns: 350px 1fr;
  min-height: 70vh;
}

/* Left Panel */
.left-panel {
  background-color: #fafafa;
  border-right: 1px solid #e0e0e0;
}

.panel-header {
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
}

.panel-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1rem;
}

.search-box {
  position: relative;
}

.search-box i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.875rem;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  outline: none;
  background-color: white;
}

.search-box input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.competency-tree {
  padding: 1rem 0;
  max-height: calc(100vh - 0px);
  overflow-y: auto;
}

/* Root Level Nodes */
.panel-hint {
  font-size: 0.75rem;
  color: #718096;
  margin-bottom: 1rem;
  font-style: italic;
}

.competency-node.selectable {
  cursor: pointer;
}

.competency-node.has-children {
  cursor: default;
  opacity: 0.7;
}

.competency-node.has-children:hover {
  background-color: #f1f5f9 !important;
  box-shadow: none !important;
}

.competency-node.selectable:hover {
  background-color: #f0fff4;
  border-left: 4px solid #48bb78;
}

.competency-node.selectable.active {
  background-color: #48bb78;
  color: white;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
}

.child-node.selectable:hover {
  background-color: #f0fff4;
  border-left: 4px solid #48bb78;
}

.child-node.selectable.active {
  background-color: #48bb78;
  color: white;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
}

.child-node.has-children {
  opacity: 0.7;
  cursor: default;
}

.leaf-icon {
  color: #48bb78;
  font-size: 0.625rem;
  margin-right: 0.5rem;
}

.competency-type-icon {
  margin-right: 0.5rem;
  font-size: 0.875rem;
  opacity: 0.7;
}

.leaf-badge {
  background: #c6f6d5;
  color: #22543d;
  font-size: 0.625rem;
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
  margin-left: auto;
  font-weight: 500;
}

.competency-node.active .leaf-badge,
.child-node.active .leaf-badge {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.selectable-count {
  color: #22543d;
  font-weight: 500;
}

.parent-count {
  color: #805ad5;
  font-weight: 500;
}

/* Tree structure lines */
.tree-lines {
  position: relative;
  width: 20px;
  height: 20px;
  margin-right: 0.5rem;
}

.vertical-line {
  position: absolute;
  left: 10px;
  top: -10px;
  width: 1px;
  height: 30px;
  background: #e2e8f0;
}

.horizontal-line {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 10px;
  height: 1px;
  background: #e2e8f0;
}

.children-container,
.grandchildren-container {
  position: relative;
}

.children-container::before,
.grandchildren-container::before {
  content: "";
  position: absolute;
  left: 25px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #e2e8f0;
}

/* Legend */
.competency-legend {
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  font-size: 0.875rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: #4a5568;
}

.legend-item:last-child {
  margin-bottom: 0;
}

/* Invalid selection state */
.invalid-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 2rem;
  color: #e53e3e;
}

.invalid-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #fc8181;
}

.invalid-selection h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #e53e3e;
  margin-bottom: 0.5rem;
}

.invalid-selection p {
  font-size: 1rem;
  color: #718096;
  margin-bottom: 0.5rem;
}

.suggestion {
  margin-top: 1.5rem;
  text-align: left;
  background: #f7fafc;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #48bb78;
}

.suggestion p {
  color: #2d3748;
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.leaf-suggestions {
  list-style: none;
  padding: 0;
  margin: 0;
}

.leaf-suggestion-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  margin-bottom: 0.25rem;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #4a5568;
}

.leaf-suggestion-item:hover {
  background: #e6fffa;
  color: #22543d;
  transform: translateX(4px);
}

.leaf-suggestion-item i {
  color: #48bb78;
  font-size: 0.75rem;
}

/* Enhanced empty state */
.selection-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #e6fffa;
  border-radius: 6px;
  color: #22543d;
  font-size: 0.875rem;
}

.selection-hint i {
  color: #38b2ac;
}

.competency-node {
  display: flex;
  flex-direction: column;
  margin: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
}

.competency-node:hover {
  background-color: #f7fafc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.competency-node.active {
  background-color: #8b5cf6;
  color: white;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.node-content {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
}

.competency-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem 0.75rem 1rem;
  margin-top: -0.25rem;
}

.chevron-icon {
  margin-right: 0.75rem;
  font-size: 0.875rem;
  color: #718096;
  transition: all 0.2s ease;
  width: 16px;
  text-align: center;
}

.competency-node.active .chevron-icon {
  color: rgba(255, 255, 255, 0.8);
}

.competency-name {
  font-weight: 600;
  font-size: 0.95rem;
  flex: 1;
}

/* Child Nodes */
.children-nodes {
  margin: 0.5rem 1rem 1rem 1rem;
}

.child-node {
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
}

.child-node:last-child {
  margin-bottom: 0;
}

node:hover {
  background-color: #f7fafc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.child-node.active {
  background-color: #8b5cf6;
  color: white;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.child-content {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
}

.node-header {
  display: flex;
  align-items: center;
  width: 100%;
}

.child-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.25rem;
  font-size: 0.8rem;
}

.child-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #4a5568;
  flex: 1;
}

.child-node.active .child-name {
  color: white;
}

.topics-count {
  font-size: 0.75rem;
  color: #718096;
  background-color: #edf2f7;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
}

.child-node.active .topics-count {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.weight-pill {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  margin-left: 0.5rem;
}

.weight-pill.high {
  background-color: #c6f6d5;
  color: #22543d;
}

.weight-pill.medium {
  background-color: #fed7aa;
  color: #c05621;
}

.weight-pill.low {
  background-color: #fecaca;
  color: #991b1b;
}

.child-node.active .weight-pill {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Right Panel */
.right-panel {
  background-color: white;
  display: flex;
  flex-direction: column;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.empty-state .empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.empty-state p {
  font-size: 1rem;
  color: #718096;
}

.trait-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.trait-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #fafafa;
}

.header-left h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.trait-count {
  font-size: 0.875rem;
  color: #718096;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Filter Panel Styles */
.filter-actions-container {
  position: relative;
}

.filter-btn {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-btn:hover,
.filter-btn.active {
  background: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
}

.filter-count {
  background: #e53e3e;
  color: white;
  border-radius: 10px;
  padding: 0.125rem 0.375rem;
  font-size: 0.75rem;
  min-width: 1.25rem;
  text-align: center;
}

.filter-panel {
  background: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin: 1rem 2rem;
  overflow: hidden;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.filter-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
}

.close-filter-btn {
  background: none;
  border: none;
  color: #718096;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-filter-btn:hover {
  background: #f7fafc;
  color: #2d3748;
}

.filter-content {
  padding: 1.5rem;
}

.filter-section {
  margin-bottom: 1.5rem;
}

.filter-section h4 {
  margin: 0 0 0.75rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.selected-traits-list {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem;
  max-height: 120px;
  overflow-y: auto;
}

.selected-trait-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f7fafc;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.selected-trait-item:last-child {
  margin-bottom: 0;
}

.remove-btn {
  background: none;
  border: none;
  color: #e53e3e;
  cursor: pointer;
  padding: 0.125rem;
  border-radius: 2px;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: #fed7d7;
}

.no-selection {
  color: #718096;
  font-style: italic;
  text-align: center;
  padding: 1rem;
}

.filter-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.action-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
}

.weight-input-group,
.delete-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.weight-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.875rem;
  outline: none;
}

.weight-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.apply-btn {
  background: #48bb78;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.apply-btn:hover:not(:disabled) {
  background: #38a169;
}

.apply-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

.delete-btn-bulk {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.delete-btn-bulk:hover:not(:disabled) {
  background: #c53030;
}

.delete-btn-bulk:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

/* Checkbox Column */
.checkbox-col {
  width: 50px;
  text-align: center;
}

.table-checkbox {
  transform: scale(1.1);
  cursor: pointer;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.875rem;
}

.search-input {
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
  width: 250px;
}

.search-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.add-btn {
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-btn:hover {
  background: #7c3aed;
}

/* Weight Alert */
.weight-alert {
  background: #fed7d7;
  border: 1px solid #feb2b2;
  color: #c53030;
  padding: 1rem 2rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 2rem;
}

.weight-alert i {
  font-size: 1.25rem;
  margin-top: 0.125rem;
}

.weight-error-item {
  margin-bottom: 0.25rem;
}

.weight-error-item:last-child {
  margin-bottom: 0;
}

/* Table Styles */
.table-container {
  flex: 1;
  overflow-x: auto;
  background: white;
  padding: 1rem 2rem;
}

.traits-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.traits-table th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.traits-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.trait-row {
  transition: all 0.2s ease;
  cursor: pointer;
}

.trait-row:hover {
  background-color: #f7fafc;
}

.trait-row.weight-invalid {
  background-color: #fed7d7;
}

.trait-row.unconfigured {
  background-color: #fef5e7;
}

.name-col {
  min-width: 200px;
}

.desc-col {
  min-width: 250px;
  max-width: 300px;
}

.weight-col {
  width: 120px;
  text-align: center;
}

.actions-col {
  width: 150px;
  text-align: center;
}

.trait-name-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.trait-name {
  font-weight: 500;
  color: #2d3748;
}

.trait-description {
  color: #718096;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.weight-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.75rem;
}

.weight-high {
  background-color: #c6f6d5;
  color: #22543d;
}

.weight-medium {
  background-color: #fed7aa;
  color: #c05621;
}

.weight-low {
  background-color: #fecaca;
  color: #991b1b;
}

.actions-container {
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}

.action-btn {
  background: none;
  border: 1px solid #e2e8f0;
  color: #718096;
  padding: 0.375rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.view-btn:hover {
  background: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
}

.edit-btn:hover {
  background: #48bb78;
  color: white;
  border-color: #48bb78;
}

.delete-btn:hover {
  background: #e53e3e;
  color: white;
  border-color: #e53e3e;
}

.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.table-empty .empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.table-empty h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.table-empty p {
  font-size: 1rem;
  color: #718096;
  margin-bottom: 1.5rem;
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #cbd5e0;
}

.table-footer {
  padding: 1rem 2rem;
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 2rem;
}

.weight-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
}

.weight-perfect {
  color: #22543d;
}

.weight-incomplete {
  color: #c05621;
}

.configure-section {
  padding: 1.5rem 2rem;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.configure-btn {
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.configure-btn:hover:not(:disabled) {
  background: #7c3aed;
}

.configure-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.add-modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.delete-modal {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: #fafafa;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
}

.edit-header-content {
  flex: 1;
}

.simple-info {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #718096;
}

.simple-info-line {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.simple-info-line:last-child {
  margin-bottom: 0;
}

.simple-label {
  font-weight: 500;
}

.simple-value {
  color: #2d3748;
}

.close-btn {
  background: none;
  border: none;
  color: #718096;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f7fafc;
  color: #2d3748;
}

.modal-body {
  flex: 1;
  padding: 1.5rem 2rem;
  overflow-y: auto;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #4a5568;
  font-size: 0.875rem;
}

.modal-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
  transition: all 0.2s ease;
}

.modal-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.modal-textarea {
  resize: vertical;
  min-height: 80px;
}

.weight-info {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #718096;
}

.current-total {
  padding: 0.25rem 0.5rem;
  background: #f7fafc;
  border-radius: 4px;
}

.field-error {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #e53e3e;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
  cursor: pointer;
  min-width: 120px;
}

.checkbox-label input[type="checkbox"] {
  transform: scale(1.1);
  cursor: pointer;
}

/* Search Section */
.search-section {
  margin-bottom: 1.5rem;
}

.search-container-full {
  position: relative;
}

.search-input-full {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
}

.search-input-full:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.create-trait-btn,
.ai-generate-btn {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.create-trait-btn {
  background: #48bb78;
  color: white;
}

.create-trait-btn:hover {
  background: #38a169;
}

.ai-generate-btn {
  background: #8b5cf6;
  color: white;
}

.ai-generate-btn:hover {
  background: #7c3aed;
}

/* Trait Cards Grid */
.trait-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.trait-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.trait-card:hover {
  border-color: #8b5cf6;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
}

.trait-card.selected {
  border-color: #8b5cf6;
  background: #f7fafc;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.trait-info {
  text-align: center;
  width: 100%;
}

.card-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.3;
}

.card-category {
  font-size: 0.75rem;
  color: #8b5cf6;
  background: #f3e8ff;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
}

.selection-checkbox {
  color: #8b5cf6;
  font-size: 1.25rem;
}

.card-description {
  margin: 0 0 0.75rem 0;
  font-size: 0.875rem;
  color: #718096;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.tag {
  font-size: 0.75rem;
  background: #edf2f7;
  color: #4a5568;
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: #718096;
}

.usage-count {
  font-weight: 500;
}

.rating {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #f6ad55;
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 0.875rem;
  color: #718096;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pagination-btn {
  background: white;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pagination-btn:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #8b5cf6;
}

.pagination-btn:disabled {
  background: #f7fafc;
  color: #a0aec0;
  cursor: not-allowed;
}

.page-indicator {
  font-size: 0.875rem;
  color: #4a5568;
  font-weight: 500;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #fafafa;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.footer-right {
  display: flex;
  gap: 0.75rem;
}

.select-all-btn {
  background: none;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.select-all-btn:hover {
  background: #f7fafc;
  border-color: #8b5cf6;
}

.selection-count {
  font-size: 0.875rem;
  color: #718096;
}

.cancel-btn {
  background: white;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.add-selected-btn {
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-selected-btn:hover:not(:disabled) {
  background: #7c3aed;
}

.add-selected-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

.save-btn {
  background: #48bb78;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn:hover:not(:disabled) {
  background: #38a169;
}

.save-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

/* Delete Modal Styles */
.delete-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.delete-icon {
  color: #e53e3e;
  font-size: 2rem;
}

.delete-message h4 {
  margin: 0 0 0.5rem 0;
  color: #2d3748;
  font-size: 1.125rem;
}

.delete-message p {
  margin: 0 0 0.5rem 0;
  color: #718096;
  line-height: 1.4;
}

.delete-confirm-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.delete-confirm-btn:hover {
  background: #c53030;
}

/* Responsive */
@media (max-width: 768px) {
  .content-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  .left-panel {
    max-height: 300px;
    overflow-y: auto;
  }

  .trait-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .header-right {
    flex-wrap: wrap;
  }

  .trait-grid {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    flex-direction: column;
  }

  .modal-footer {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .footer-left,
  .footer-right {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .trait-configuration {
    padding: 1rem;
  }

  .modal-content,
  .add-modal-content {
    width: 95%;
    margin: 0.5rem;
  }

  .traits-table {
    font-size: 0.75rem;
  }

  .traits-table th,
  .traits-table td {
    padding: 0.5rem;
  }
}

/* Animation for card selection */
@keyframes cardSelect {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.02);
  }

  100% {
    transform: scale(1);
  }
}

.trait-card.selected {
  animation: cardSelect 0.3s ease;
}

/* Focus states for accessibility */
.trait-card:focus,
.competency-node:focus,
.child-node:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

.pagination-btn:focus,
.action-btn:focus,
.add-btn:focus,
.filter-btn:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

.modal-input:focus {
  outline: none;
}

/* Print styles */
@media print {

  .modal-overlay,
  .action-btn,
  .add-btn,
  .configure-btn {
    display: none !important;
  }
}

/* Custom scrollbar for modals */
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
